import {FC, useEffect, useRef, useState} from 'react'
import {api} from '../../services/api'
import {usePaginated} from '../../hooks/usePaginated'
import {Pagination} from '../../components/Pagination'

interface ApiEvent {
  id: string
  status: string
  eventName?: string
  eventType?: {id: number; label: string; icon: string}
  eventDate?: string
  startTime?: string
  venueAddress?: string
  guestCount?: string
  price?: number | string
  user?: {id: string; firstName: string; lastName: string}
  createdAt: string
}

function mapStatus(s: string): 'approved' | 'pending' | 'rejected' | 'draft' {
  if (s === 'published') return 'approved'
  if (s === 'draft')     return 'pending'
  if (s === 'rejected')  return 'rejected'
  return 'pending'
}

function extractCity(addr?: string): string {
  if (!addr) return '—'
  const parts = addr.split(',')
  return parts[0]?.trim() || addr
}

function fmtDate(d?: string): string {
  if (!d) return '—'
  try { return new Date(d).toISOString().slice(0, 10) } catch { return d }
}

const CAT_COLORS: Record<string, {bg: string; color: string}> = {
  music:         {bg: '#dbeafe', color: '#1d4ed8'},
  technology:    {bg: '#e0f2fe', color: '#0369a1'},
  health:        {bg: '#dcfce7', color: '#15803d'},
  entertainment: {bg: '#ede9fe', color: '#7c3aed'},
  food:          {bg: '#fef9c3', color: '#a16207'},
  business:      {bg: '#e0f2fe', color: '#0369a1'},
  arts:          {bg: '#fce7f3', color: '#be185d'},
  social:        {bg: '#d1fae5', color: '#065f46'},
  wedding:       {bg: '#fce7f3', color: '#be185d'},
  sports:        {bg: '#ffedd5', color: '#c2410c'},
}

function catStyle(label?: string) {
  const key = (label ?? '').toLowerCase()
  return CAT_COLORS[key] ?? {bg: '#f1f5f9', color: '#475569'}
}

const statusStyle = (s: string) => {
  if (s === 'approved') return {bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0'}
  if (s === 'rejected') return {bg: '#fee2e2', color: '#dc2626', border: '#fca5a5'}
  return {bg: '#fef9c3', color: '#a16207', border: '#fde68a'}
}

// ── Action Menu ───────────────────────────────────────────────────────────────
const EventActionMenu: FC<{event: ApiEvent; featured: boolean; onAction: (a: string, e: ApiEvent) => void}> = ({event, featured, onAction}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const items = [
    {
      label: featured ? 'Unfeature' : 'Feature', action: 'feature',
      icon: <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><polygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2'/></svg>,
    },
    {
      label: 'View Attendees', action: 'attendees',
      icon: <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'><path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2'/><circle cx='9' cy='7' r='4'/><path d='M23 21v-2a4 4 0 0 0-3-3.87'/><path d='M16 3.13a4 4 0 0 1 0 7.75'/></svg>,
    },
  ]

  return (
    <div ref={ref} style={{position: 'relative', display: 'inline-block'}}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, color: '#94a3b8', fontSize: 20, lineHeight: 1}}
      >⋮</button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', background: '#fff',
          border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 200, minWidth: 180, overflow: 'hidden', marginTop: 4,
        }}>
          {items.map(item => (
            <button
              key={item.action}
              onClick={() => { onAction(item.action, event); setOpen(false) }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', textAlign: 'left', padding: '10px 16px',
                background: 'none', border: 'none', fontSize: 14, cursor: 'pointer',
                color: '#374151',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              {item.icon}{item.label}
            </button>
          ))}
          <div style={{height: 1, background: '#f1f5f9', margin: '2px 0'}} />
          <button
            onClick={() => { onAction('delete', event); setOpen(false) }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', textAlign: 'left', padding: '10px 16px',
              background: 'none', border: 'none', fontSize: 14, cursor: 'pointer',
              color: '#ef4444',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#fef2f2')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <polyline points='3 6 5 6 21 6'/><path d='M19 6l-1 14H6L5 6'/>
              <path d='M10 11v6M14 11v6'/><path d='M9 6V4h6v2'/>
            </svg>
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const EventsPage: FC = () => {
  const [statusFilter, setStatusFilter] = useState('all')
  const [catFilter, setCatFilter]       = useState('all')
  const [featuredIds, setFeaturedIds]   = useState<Set<string>>(new Set())

  const {items, total, page, setPage, search, setSearch, limit, loading, error, reload} =
    usePaginated<ApiEvent>(
      (p, l, s) => api.getEventsPaged(p, l, s, {
        status: statusFilter !== 'all' ? statusFilter : undefined,
      }),
      {limit: 20, deps: [statusFilter]}
    )

  // Category filter is applied client-side over the current page (no server param).
  const filtered = items.filter(e => {
    const label = (e.eventType?.label ?? '').toLowerCase()
    return catFilter === 'all' || label === catFilter.toLowerCase()
  })

  const categories = Array.from(new Set(items.map(e => e.eventType?.label).filter(Boolean)))

  const handleAction = (action: string, event: ApiEvent) => {
    if (action === 'feature') {
      setFeaturedIds(prev => {
        const next = new Set(prev)
        next.has(event.id) ? next.delete(event.id) : next.add(event.id)
        return next
      })
    }
    if (action === 'attendees') alert(`View attendees for "${event.eventName}" — coming soon`)
    if (action === 'delete') {
      if (window.confirm(`Delete "${event.eventName}"?`)) {
        api.deleteEvent(event.id).then(reload).catch(e => alert(e.message))
      }
    }
  }

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 24}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Events</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Manage and moderate events on the platform</p>
      </div>

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
        padding: '16px 20px', marginBottom: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div style={{position: 'relative', flex: '1 1 260px'}}>
          <svg style={{position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
            <circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>
          </svg>
          <input
            type='text' placeholder='Search events...' value={search}
            onChange={e => setSearch(e.target.value)}
            style={{width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc', outline: 'none', boxSizing: 'border-box'}}
          />
        </div>

        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          style={{flex: '0 0 180px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#374151', background: '#f8fafc', cursor: 'pointer', outline: 'none'}}>
          <option value='all'>All Categories</option>
          {categories.map(c => <option key={c} value={c!}>{c}</option>)}
        </select>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{flex: '0 0 160px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#374151', background: '#f8fafc', cursor: 'pointer', outline: 'none'}}>
          <option value='all'>All Status</option>
          <option value='approved'>Approved</option>
          <option value='pending'>Pending</option>
          <option value='rejected'>Rejected</option>
        </select>

        <button onClick={reload} disabled={loading}
          style={{display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 500, color: '#374151', background: '#f8fafc', cursor: 'pointer'}}>
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' style={{animation: loading ? 'spin 1s linear infinite' : 'none'}}>
            <polyline points='23 4 23 10 17 10'/><polyline points='1 20 1 14 7 14'/>
            <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15'/>
          </svg>
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <span>{error}</span>
          <button onClick={reload} style={{color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13}}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'}}>
        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0'}}>
            <div style={{width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}} />
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#f8fafc', borderBottom: '1px solid #e2e8f0'}}>
                  {['EVENT', 'CATEGORY', 'DATE', 'LOCATION', 'PRICE', 'ATTENDEES', 'STATUS', 'ACTIONS'].map(h => (
                    <th key={h} style={{padding: '11px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap'}}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => {
                  const mapped  = mapStatus(e.status)
                  const ss      = statusStyle(mapped)
                  const cs      = catStyle(e.eventType?.label)
                  const city    = extractCity(e.venueAddress)
                  const price   = e.price != null
                    ? (Number(e.price) === 0 ? 'Free' : `$${e.price}`)
                    : '—'
                  const attendees = e.guestCount ? parseInt(e.guestCount) : null

                  return (
                    <tr key={e.id}
                      style={{borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none'}}
                      onMouseEnter={ev => (ev.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
                    >
                      {/* Event */}
                      <td style={{padding: '14px 20px', minWidth: 200}}>
                        <div style={{display: 'flex', alignItems: 'flex-start', gap: 8}}>
                          {featuredIds.has(e.id) && (
                            <span style={{color: '#f59e0b', fontSize: 14, marginTop: 2, flexShrink: 0}}>★</span>
                          )}
                          <div>
                            <div style={{fontWeight: 700, color: '#0f172a', fontSize: 14}}>{e.eventName || '(Untitled)'}</div>
                            <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{city}</div>
                          </div>
                        </div>
                      </td>

                      {/* Category */}
                      <td style={{padding: '14px 20px', whiteSpace: 'nowrap'}}>
                        {e.eventType ? (
                          <span style={{...cs, padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500}}>
                            {e.eventType.label}
                          </span>
                        ) : <span style={{color: '#94a3b8'}}>—</span>}
                      </td>

                      {/* Date */}
                      <td style={{padding: '14px 20px', color: '#374151', whiteSpace: 'nowrap'}}>
                        {fmtDate(e.eventDate)}
                      </td>

                      {/* Location */}
                      <td style={{padding: '14px 20px', color: '#374151', whiteSpace: 'nowrap'}}>
                        {city}
                      </td>

                      {/* Price */}
                      <td style={{padding: '14px 20px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap'}}>
                        {price}
                      </td>

                      {/* Attendees */}
                      <td style={{padding: '14px 20px', whiteSpace: 'nowrap'}}>
                        {attendees !== null ? (
                          <span style={{display: 'flex', alignItems: 'center', gap: 5, color: '#3b82f6', fontWeight: 600}}>
                            <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                              <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2'/>
                              <circle cx='9' cy='7' r='4'/>
                              <path d='M23 21v-2a4 4 0 0 0-3-3.87'/>
                              <path d='M16 3.13a4 4 0 0 1 0 7.75'/>
                            </svg>
                            {attendees.toLocaleString()}
                          </span>
                        ) : <span style={{color: '#94a3b8'}}>—</span>}
                      </td>

                      {/* Status */}
                      <td style={{padding: '14px 20px'}}>
                        <span style={{padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`}}>
                          {mapped}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{padding: '14px 20px', textAlign: 'right'}}>
                        <EventActionMenu event={e} featured={featuredIds.has(e.id)} onAction={handleAction} />
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{padding: '48px 20px', textAlign: 'center', color: '#94a3b8'}}>
                      No events found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div style={{padding: '12px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: 13, color: '#94a3b8'}}>Showing {filtered.length} of {total} events</span>
            <div style={{display: 'flex', gap: 8}}>
              {[
                {label: 'Approved', count: items.filter(e => mapStatus(e.status) === 'approved').length, bg: '#dcfce7', color: '#16a34a'},
                {label: 'Pending',  count: items.filter(e => mapStatus(e.status) === 'pending').length,  bg: '#fef9c3', color: '#a16207'},
              ].map(s => (
                <span key={s.label} style={{padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: s.bg, color: s.color}}>
                  {s.label}: {s.count}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

const EventsPageWrapper: FC = () => <EventsPage />

export {EventsPageWrapper}
